import React, { useState } from "react";
import { X, Minus, Plus, Loader2, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { VEYRO } from "@/lib/veyro";
import { createShopifyCheckout } from "@/lib/shopify";
import { trackPixelEvent } from "@/lib/metaPixel";

const VeyroCart = () => {
  const { isOpen, closeCart, setQty, pricing, shopifyProduct, protection, toggleProtection, protectionProduct, color, variantId } = useCart();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (pricing.qty < 1) {
      toast.error("Your cart is empty");
      return;
    }
    setLoading(true);
    trackPixelEvent("InitiateCheckout", {
      content_name: VEYRO.name,
      content_type: "product",
      contents: [{ id: VEYRO.pixelId, quantity: pricing.qty }],
      num_items: pricing.count,
      value: pricing.total,
      currency: "USD",
    });
    try {
      if (!variantId) {
        toast.error("Checkout is being connected — please try again in a little while.");
        setLoading(false);
        return;
      }
      const lines = [{ merchandiseId: variantId, quantity: pricing.qty }];
      if (pricing.protection?.variantId) {
        lines.push({ merchandiseId: pricing.protection.variantId, quantity: 1 });
      }
      const url = await createShopifyCheckout(lines);
      window.location.href = url;
    } catch (err) {
      console.error(err);
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <div
        data-testid="cart-overlay"
        onClick={closeCart}
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[90] transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed right-0 top-0 h-full w-full sm:w-[440px] bg-[#0D0F15] shadow-2xl z-[100] flex flex-col border-l border-[#282D3C] transform transition-transform duration-300 ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between p-6 border-b border-[#282D3C]">
          <h3 className="text-2xl font-black tracking-tight text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Your cart
          </h3>
          <button data-testid="cart-close-btn" onClick={closeCart} aria-label="Close" className="p-2 text-slate-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {pricing.qty > 0 && (
          <div className="px-6 pt-4">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
              <span className="flex items-center gap-1.5"><Truck size={13} className="text-[#00E5FF]" />
                {pricing.freeShipUnlocked ? "Free shipping unlocked" : "Free shipping progress"}
              </span>
              <span className="font-bold text-[#00E5FF]">{Math.round(pricing.freeShipProgress * 100)}%</span>
            </div>
            <div data-testid="free-ship-bar" className="h-1.5 rounded-full bg-[#282D3C] overflow-hidden">
              <div className="h-full bg-[#00E5FF] rounded-full transition-all duration-500" style={{ width: `${pricing.freeShipProgress * 100}%` }} />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {pricing.qty === 0 ? (
            <div data-testid="cart-empty" className="text-center py-20">
              <p className="text-slate-400">Your cart is empty.</p>
              <button onClick={closeCart} className="mt-6 inline-flex items-center justify-center rounded-full border border-slate-600 text-slate-200 px-6 py-3 text-sm font-bold hover:bg-white hover:text-[#090A0E] transition-colors">
                Keep shopping
              </button>
            </div>
          ) : (
            <>
              <div data-testid="cart-line-veyro" className="flex gap-4 p-4 rounded-2xl bg-[#161922] border border-[#282D3C]">
                <img src={shopifyProduct?.image || VEYRO.images.black} alt={VEYRO.shortName} className="w-20 h-20 object-cover rounded-xl bg-black/40" />
                <div className="flex-1">
                  <h4 className="font-bold text-white leading-snug">{VEYRO.shortName}</h4>
                  <p data-testid="cart-line-color" className="text-[11px] text-slate-500 mt-0.5">Color: <span className="text-slate-300 font-bold">{color}</span></p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    <span className="font-bold text-[#00E5FF]">${pricing.price.toFixed(2)}</span>
                    {pricing.compareAt && <span className="line-through ml-1.5">${pricing.compareAt.toFixed(2)}</span>}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="inline-flex items-center rounded-full border border-slate-700 bg-[#0D0F15]">
                      <button data-testid="cart-qty-dec" onClick={() => setQty(pricing.qty - 1)} className="p-2 text-slate-300"><Minus size={14} /></button>
                      <span data-testid="cart-qty-value" className="w-8 text-center text-sm font-bold text-white">{pricing.qty}</span>
                      <button data-testid="cart-qty-inc" onClick={() => setQty(pricing.qty + 1)} className="p-2 text-slate-300"><Plus size={14} /></button>
                    </div>
                    <button data-testid="cart-remove" onClick={() => setQty(0)} className="text-xs text-slate-500 hover:text-[#00E5FF] underline underline-offset-4">
                      Remove
                    </button>
                  </div>
                </div>
              </div>

              {protectionProduct && (
                <label
                  data-testid="cart-protection-offer"
                  className="flex items-center gap-3 p-4 rounded-2xl border border-dashed border-[#00E5FF]/30 bg-[#00E5FF]/5 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    data-testid="cart-protection-toggle"
                    checked={protection}
                    onChange={(e) => toggleProtection(e.target.checked)}
                    className="w-5 h-5 accent-[#00E5FF] shrink-0 cursor-pointer"
                  />
                  <ShieldCheck size={20} className="text-[#00E5FF] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white">Add Shipping Protection</div>
                    <div className="text-xs text-slate-400">Covers loss, theft & damage · +${protectionProduct.price.toFixed(2)}</div>
                  </div>
                </label>
              )}
            </>
          )}
        </div>

        {pricing.qty > 0 && (
          <div className="border-t border-[#282D3C] p-6 space-y-3 bg-[#0D0F15]">
            {pricing.savings > 0 && (
              <div className="flex justify-between text-sm text-[#00E5FF] font-bold">
                <span>You're saving</span>
                <span data-testid="cart-savings">${pricing.savings.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm text-slate-400">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">${pricing.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-black text-white border-t border-[#282D3C] pt-3">
              <span>Total</span>
              <span data-testid="cart-total">${pricing.total.toFixed(2)}</span>
            </div>
            <button
              data-testid="cart-checkout-btn"
              onClick={handleCheckout}
              disabled={loading}
              className="w-full inline-flex items-center justify-center bg-[#00E5FF] hover:bg-[#33ECFF] text-[#090A0E] rounded-full px-6 py-4 text-base font-bold transition-colors duration-200 disabled:opacity-70"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Redirecting...</span>
              ) : (
                `Checkout · $${pricing.total.toFixed(2)}`
              )}
            </button>
            <p className="text-[11px] text-center text-slate-500">Secure checkout via Shopify</p>
          </div>
        )}
      </aside>
    </>
  );
};

export default VeyroCart;
